import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { faqItems } from '@/mocks/comics';

const stats = [
  { label: '漫画总量', value: '10,000+', icon: 'ri-book-open-line' },
  { label: '累计用户', value: '500万+', icon: 'ri-user-heart-line' },
  { label: '日更新量', value: '100+', icon: 'ri-flashlight-line' },
  { label: '运营年限', value: '6年', icon: 'ri-calendar-check-line' },
];

const reputationItems = [
  {
    title: '海量内容资源',
    desc: '收录超过10,000部韩国漫画作品，涵盖恋爱、奇幻、动作、悬疑等多种类型，满足不同读者的阅读偏好。每日持续更新最新连载内容。',
    icon: 'ri-database-2-line',
  },
  {
    title: '极致阅读体验',
    desc: '独创下拉式阅读模式，无需翻页，只需滚动即可连续阅读。适配电脑、平板和手机端，随时随地畅享韩漫。页面加载迅速，阅读流畅不卡顿。',
    icon: 'ri-smartphone-line',
  },
  {
    title: '完全免费',
    desc: '韩漫网始终坚持免费阅读原则，所有漫画内容均可免费在线观看，无需任何付费或订阅。我们相信优质的漫画应该被更多人看到。',
    icon: 'ri-vip-crown-line',
  },
  {
    title: '更新及时',
    desc: '专业的更新团队确保热门连载作品第一时间同步更新，让读者不错过任何精彩内容。新番上架及时，热门推荐精准。',
    icon: 'ri-timer-flash-line',
  },
];

export default function About() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
    website_alt: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMsg('感谢您的留言，我们会尽快回复！');
      return;
    }
    formData.delete('website_alt');

    setFormStatus('submitting');
    try {
      const response = await fetch('https://readdy.ai/api/form/d9pd0ciqs38sj6jc7m20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        setFormMsg('感谢您的留言，我们会尽快回复！');
        setContactForm({ name: '', email: '', message: '', website_alt: '' });
      } else {
        setFormStatus('error');
        const msg = (parsed as { meta?: { message?: string } }).meta?.message
          || (parsed as { message?: string }).message
          || '提交失败，请稍后再试';
        setFormMsg(msg);
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后再试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫网 - 韩国漫画全集免费在线阅读平台',
    description: '韩漫网是专业的韩国漫画在线阅读平台，拥有好看的韩国漫画大全，实现韩国漫画全集免费看，专属下拉式模式让韩漫在线阅读更轻松。',
    url: 'https://www.hanmanwang.com/about',
    publisher: {
      '@type': 'Organization',
      name: '韩漫网',
      url: 'https://www.hanmanwang.com',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫网',
    url: 'https://www.hanmanwang.com',
    description: '专业的韩国漫画在线阅读平台，提供高质量免费的韩漫资源。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@hanmanwang.com',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <main>
            {/* Hero Banner */}
            <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20abstract%20illustration%20with%20soft%20coral%20and%20cream%20tones%2C%20gentle%20gradient%20flow%2C%20elegant%20minimalist%20composition%2C%20calming%20atmosphere%2C%20artistic%20background%20with%20flowing%20curves%2C%20warm%20lighting%2C%20professional%20aesthetic%2C%20no%20text%2C%20abstract%20organic%20shapes&width=1600&height=600&seq=about-hero&orientation=landscape"
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-4">
                  关于韩漫网
                </h1>
                <p className="text-sm md:text-base text-background-200/90 max-w-2xl">
                  致力于为漫画爱好者提供最优质的韩国漫画在线阅读体验
                </p>
              </div>
            </section>

            {/* Brand Introduction */}
            <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <div className="max-w-3xl mx-auto text-center mb-10">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                  品牌介绍
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  韩漫网成立于2020年，是国内领先的韩国漫画在线阅读平台。我们专注于为中文读者提供高质量、多样化的韩漫内容，涵盖恋爱、奇幻、动作、悬疑、搞笑、日常等多种类型。凭借独创的下拉式阅读模式，让读者在电脑和手机上都能获得流畅舒适的阅读体验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mt-3">
                  我们始终秉持"韩国漫画全集免费看"的理念，致力于降低优质漫画的阅读门槛，让更多人能够轻松接触并享受韩国漫画的魅力。韩漫网拥有好看的韩国漫画大全，无论是热门连载还是经典作品，你都能在这里找到。
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-background-100 rounded-lg border border-background-200/70 p-5 text-center"
                  >
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                      <i className={`${stat.icon} text-primary-600 text-lg`}></i>
                    </div>
                    <p className="text-2xl font-bold text-foreground-900">{stat.value}</p>
                    <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Reputation / Features */}
            <section className="bg-background-100 py-10 md:py-14">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-10">
                  为什么选择韩漫网
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                  {reputationItems.map((item) => (
                    <div
                      key={item.title}
                      className="flex gap-4 bg-background-50 rounded-lg border border-background-200/70 p-5"
                    >
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                        <i className={`${item.icon} text-accent-600 text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-2">{item.title}</h3>
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* FAQ in About */}
            <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-8">
                  常见问题
                </h2>
                <div className="flex flex-col gap-3">
                  {faqItems.map((item, index) => (
                    <details
                      key={index}
                      className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden"
                    >
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors list-none">
                        <h4 className="text-sm font-medium">{item.question}</h4>
                        <i className="ri-add-line group-open:ri-subtract-line text-foreground-400 transition-all"></i>
                      </summary>
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {item.answer}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </section>

            {/* Contact Section */}
            <section className="bg-background-100 py-10 md:py-14">
              <div className="max-w-3xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-8">
                  联系我们
                </h2>
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 text-center">
                    <div className="p-4">
                      <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                        <i className="ri-mail-line text-primary-600 text-lg"></i>
                      </div>
                      <p className="text-xs text-foreground-500">邮箱</p>
                      <p className="text-sm font-medium text-foreground-800 mt-1 whitespace-nowrap">contact@hanmanwang.com</p>
                    </div>
                    <div className="p-4">
                      <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-3">
                        <i className="ri-qq-line text-accent-600 text-lg"></i>
                      </div>
                      <p className="text-xs text-foreground-500">客服QQ</p>
                      <p className="text-sm font-medium text-foreground-800 mt-1">1234567890</p>
                    </div>
                    <div className="p-4">
                      <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-3">
                        <i className="ri-time-line text-secondary-600 text-lg"></i>
                      </div>
                      <p className="text-xs text-foreground-500">工作时间</p>
                      <p className="text-sm font-medium text-foreground-800 mt-1">9:00 - 22:00</p>
                    </div>
                  </div>

                  <form className="flex flex-col gap-4" data-readdy-form="true" onSubmit={handleContactSubmit}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input
                        type="text"
                        name="name"
                        placeholder="您的昵称"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        className="px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      />
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        className="px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      />
                    </div>
                    <textarea
                      name="message"
                      placeholder="请输入您的留言内容（最多500字）"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      maxLength={500}
                      rows={4}
                      required
                      className="px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                    />
                    <input
                      type="text"
                      name="website_alt"
                      value={contactForm.website_alt}
                      onChange={(e) => setContactForm({ ...contactForm, website_alt: e.target.value })}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 px-6 py-2.5 rounded-md text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-secondary-600">{formMsg}</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-primary-600">{formMsg}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
}